import { isNonEmpty } from '@sgme/fp';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { NumericFieldsContext } from 'components/common-form/contexts/NumericFieldsContext';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import type { TenorFieldUnitOption, TenorFieldValue } from 'components/shared/TenorField/TenorField';
import { TenorField as Tenor } from 'components/shared/TenorField/TenorField';
import type { IntlKey } from 'locales';
import type { CustomizableTenorFieldData, CustomizableTenorFieldMetadata } from 'models/forms/fields';
import { FC, useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

export const TenorField: FC<ProductNodeBuilderProps> = ({ node: { id }, workspaceType, tabIndex }) => {
  const [value, setValue] = useData<CustomizableTenorFieldData>(id);
  const translationKey = useFieldTranslationKey(id);
  const isInvalid = useIsFieldInvalid(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const intl = useIntl();

  const fieldMetadata: Readonly<CustomizableTenorFieldMetadata> | undefined = useFieldMetadata(id);

  const {
    onEditingKeyDown: onKeyDown,
    lostInputBeforeEditing,
    isCellInactive,
    isFocused,
    isSelectAll,
  } = useContext(BatchNavigationContext);

  const numericFieldCtx = useContext(NumericFieldsContext);

  const availableUnits: TenorFieldUnitOption[] = (fieldMetadata?.availableUnits || []).map((u: string) => ({
    label: intl.formatMessage({ id: `form.${translationKey}.option.${u}` as any }),
    shortcut: u.charAt(0).toLowerCase(),
    value: u,
  }));

  const defaultTenorValue: TenorFieldValue = {
    value: 0,
    unit: isNonEmpty(availableUnits) ? availableUnits[0].value : '%',
  };

  const tenorValue: TenorFieldValue = value?.currentValue ?? defaultTenorValue;
  const tenorCustomized: boolean = value?.isCustomized ?? false;

  const lostInputTenorFieldValue = useMemo<TenorFieldValue | undefined>(() => {
    return lostInputBeforeEditing ? { value: Number(lostInputBeforeEditing), unit: tenorValue.unit } : undefined;
  }, [lostInputBeforeEditing, tenorValue]);

  const onChange = useCallback(
    (tenor: TenorFieldValue) => {
      if (lostInputTenorFieldValue && lostInputTenorFieldValue.value === tenorValue.value) {
        return;
      } else {
        setValue({ currentValue: tenor, isCustomized: value ? value.isCustomized : false });
      }
    },
    [lostInputTenorFieldValue, setValue, tenorValue.value, value],
  );

  const onUncustomize = useCallback(
    () => setValue({ currentValue: value?.currentValue ?? tenorValue, isCustomized: false }),
    [setValue, value, tenorValue],
  );

  const placeholder = useMemo(
    () => intl.formatMessage({ id: (fieldMetadata?.placeholderKey ?? `form.${id}`) as IntlKey }),
    [intl, id, fieldMetadata],
  );

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as IntlKey}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      <Tenor
        className={isCellInactive ? 'pe-none' : ''}
        id={id}
        availableUnits={availableUnits}
        value={tenorValue}
        onChange={onChange}
        onUncustomize={onUncustomize}
        hasErrors={isInvalid}
        placeholder={placeholder}
        isCustomized={tenorCustomized}
        prependLabelKey={fieldMetadata?.prependLabelKey}
        hasIncrementers={numericFieldCtx.useIncrementers}
        incrementersLayout={numericFieldCtx.incrementersLayout}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        isFocused={isFocused}
        isSelectAll={isSelectAll}
        lostInputTenorFieldValue={lostInputTenorFieldValue}
      />
    </BlockOrientationFormGroup>
  );
};

TenorField.displayName = 'TenorField';
